import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import apiClient from '../../api/client';
import { APP_CONFIG } from '../../config/constants';

// Character limits for profile fields
const FIELD_LIMITS = {
    firstName: 40,
    lastName: 40,
    serverName: 40,
    phone: 20,
    alternativeEmail: 100,
    discordUsername: 50,
    serverWebsite: 200,
    addressNumber: 10,
    addressComplement: 100
};

interface ProfileData {
    firstName: string;
    lastName: string;
    phone: string;
    alternativeEmail: string;
    avatarUrl: string;
    discordUsername: string;
    linkingCode: string;
    serverName: string;
    serverWebsite: string;
    serverLogoUrl: string;
    addressStreet: string;
    addressNumber: string;
    addressComplement: string;
    addressNeighborhood: string;
    addressCity: string;
    addressState: string;
    addressZipCode: string;
}

interface EditProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProfileUpdated?: () => void;
}

export function EditProfileModal({ isOpen, onClose, onProfileUpdated }: EditProfileModalProps) {
    const [loading, setLoading] = useState(false);
    const [fetchingProfile, setFetchingProfile] = useState(true);
    const [loadingCep, setLoadingCep] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [uploadingServerLogo, setUploadingServerLogo] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [serverLogoPreview, setServerLogoPreview] = useState<string | null>(null);
    const [hasPurchase, setHasPurchase] = useState(false);

    const [profile, setProfile] = useState<ProfileData>({
        firstName: '',
        lastName: '',
        phone: '',
        alternativeEmail: '',
        avatarUrl: '',
        discordUsername: '',
        linkingCode: '',
        serverName: '',
        serverWebsite: '',
        serverLogoUrl: '',
        addressStreet: '',
        addressNumber: '',
        addressComplement: '',
        addressNeighborhood: '',
        addressCity: '',
        addressState: '',
        addressZipCode: ''
    });

    useEffect(() => {
        if (isOpen) {
            fetchProfile();
        }
    }, [isOpen]);

    const fetchProfile = async () => {
        setFetchingProfile(true);
        try {
            const response = await apiClient.get('/profile');
            const profileData = response.data;

            setProfile({
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                phone: profileData.phone || '',
                alternativeEmail: profileData.alternativeEmail || '',
                avatarUrl: profileData.avatarUrl || '',
                discordUsername: profileData.discordUsername || '',
                linkingCode: profileData.linkingCode || '',
                serverName: profileData.serverName || '',
                serverWebsite: profileData.serverWebsite || '',
                serverLogoUrl: profileData.serverLogoUrl || '',
                addressStreet: profileData.addressStreet || '',
                addressNumber: profileData.addressNumber || '',
                addressComplement: profileData.addressComplement || '',
                addressNeighborhood: profileData.addressNeighborhood || '',
                addressCity: profileData.addressCity || '',
                addressState: profileData.addressState || '',
                addressZipCode: profileData.addressZipCode || ''
            });

            // Set purchase status
            setHasPurchase(profileData.hasPurchase || false);
        } catch (err) {
            console.error('Error fetching profile:', err);
        } finally {
            setFetchingProfile(false);
        }
    };

    const handleChange = (field: keyof ProfileData, value: string) => {
        setProfile(prev => ({ ...prev, [field]: value }));
        setError('');
        setSuccess('');
    };

    const handleCepChange = async (cep: string) => {
        // Remove caracteres não numéricos
        const cleanCep = cep.replace(/\D/g, '');

        // Atualiza o CEP com formatação
        const formattedCep = cleanCep.length > 5
            ? `${cleanCep.slice(0, 5)}-${cleanCep.slice(5, 8)}`
            : cleanCep;

        setProfile(prev => ({ ...prev, addressZipCode: formattedCep }));
        setError('');
        setSuccess('');

        // Busca endereço quando o CEP tiver 8 dígitos
        if (cleanCep.length === 8) {
            setLoadingCep(true);
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
                const data = await response.json();

                if (data.erro) {
                    setError('CEP não encontrado');
                    setLoadingCep(false);
                    return;
                }

                // Preenche automaticamente os campos de endereço
                setProfile(prev => ({
                    ...prev,
                    addressStreet: data.logradouro || '',
                    addressNeighborhood: data.bairro || '',
                    addressCity: data.localidade || '',
                    addressState: data.uf || '',
                    addressZipCode: formattedCep
                }));
            } catch (err) {
                console.error('Erro ao buscar CEP:', err);
                setError('Erro ao buscar CEP. Verifique sua conexão.');
            } finally {
                setLoadingCep(false);
            }
        }
    };

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo de arquivo
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Apenas imagens JPG, PNG ou WEBP são permitidas');
            return;
        }

        // Validar tamanho (5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('A imagem deve ter no máximo 5MB');
            return;
        }

        // Validar dimensões mínimas
        const img = new Image();
        img.onload = async () => {
            if (img.width < 200 || img.height < 200) {
                setError('A imagem deve ter no mínimo 200x200 pixels');
                setAvatarPreview(null);
                return;
            }

            setAvatarPreview(URL.createObjectURL(file));
            setError('');

            // Upload automático
            await uploadAvatar(file);
        };
        img.src = URL.createObjectURL(file);
    };

    const uploadAvatar = async (file: File) => {
        setUploadingAvatar(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await apiClient.post('/profile/upload-avatar', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });


            setProfile(prev => ({ ...prev, avatarUrl: response.data.avatarUrl }));
            setSuccess('Avatar enviado com sucesso!');
            setAvatarPreview(null);
            if (onProfileUpdated) {
                onProfileUpdated();
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Erro ao fazer upload do avatar';
            setError(errorMessage);
            setAvatarPreview(null);
            setUploadingAvatar(false);
        }
    };

    const handleRemoveAvatar = async () => {
        if (!profile.avatarUrl) return;

        setUploadingAvatar(true);
        setError('');

        try {
            await apiClient.delete('/profile/avatar');
            setProfile(prev => ({ ...prev, avatarUrl: '' }));
            setSuccess('Avatar removido com sucesso!');

            if (onProfileUpdated) {
                onProfileUpdated();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao remover avatar');
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleServerLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo de arquivo
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Apenas imagens JPG, PNG ou WEBP são permitidas');
            return;
        }

        // Validar tamanho (5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('A imagem deve ter no máximo 5MB');
            return;
        }

        // Validar dimensões mínimas
        const img = new Image();
        img.onload = async () => {
            if (img.width < 100 || img.height < 100) {
                setError('A logo deve ter no mínimo 100x100 pixels');
                setServerLogoPreview(null);
                return;
            }
            setServerLogoPreview(URL.createObjectURL(file));
            setError('');

            await uploadServerLogo(file);
        };
        img.src = URL.createObjectURL(file);
    };

    const uploadServerLogo = async (file: File) => {
        setUploadingServerLogo(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('serverLogo', file);

            const response = await apiClient.post('/profile/upload-server-logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setProfile(prev => ({ ...prev, serverLogoUrl: response.data.serverLogoUrl }));
            setSuccess('Logo do servidor enviada com sucesso!');
            setServerLogoPreview(null);

            if (onProfileUpdated) {
                onProfileUpdated();
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Erro ao fazer upload da logo';
            setError(errorMessage);
            setServerLogoPreview(null);
        } finally {
            setUploadingServerLogo(false);
        }
    };

    const handleRemoveServerLogo = async () => {
        if (!profile.serverLogoUrl) return;

        setUploadingServerLogo(true);
        setError('');

        try {
            await apiClient.delete('/profile/server-logo');
            setProfile(prev => ({ ...prev, serverLogoUrl: '' }));
            setSuccess('Logo do servidor removida com sucesso!');

            if (onProfileUpdated) {
                onProfileUpdated();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao remover logo');
        } finally {
            setUploadingServerLogo(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await apiClient.put('/profile', profile);
            setSuccess('Perfil atualizado com sucesso!');
            if (onProfileUpdated) {
                onProfileUpdated();
            }
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erro ao atualizar perfil');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
                                {/* Header com gradiente */}
                                <div className="bg-brand-blue px-8 py-6 relative overflow-hidden">
                                    <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -mr-20 -mt-20"></div>
                                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full -ml-16 -mb-16"></div>
                                    </div>
                                    <div className="relative flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                                                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Dialog.Title as="h3" className="text-2xl font-bold text-white">
                                                    Editar Perfil
                                                </Dialog.Title>
                                                <p className="text-white/90 text-sm mt-1">Complete suas informações pessoais</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all group"
                                        >
                                            <svg className="w-5 h-5 text-white group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                {/* Conteúdo */}
                <div className="p-8 max-h-[calc(100vh-240px)] overflow-y-auto custom-scrollbar">
                    {fetchingProfile ? (
                                        <div className="text-center py-8">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
                                            <p className="text-gray-500 mt-4">Carregando perfil...</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            {/* Informações Pessoais com Avatar */}
                                            <div className="bg-gradient-to-br from-brand-blue-light to-blue-50 rounded-2xl p-6 border border-blue-100">
                                                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                    <span className="text-xl">👤</span>
                                                    Informações Pessoais
                                                </h4>

                                                {/* Layout com avatar à esquerda e campos à direita */}
                                                <div className="flex flex-col md:flex-row gap-4 items-start">
                                                    {/* Avatar à esquerda */}
                                                    <div className="relative group flex-shrink-0">
                                                        <input
                                                            type="file"
                                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                                            onChange={handleAvatarChange}
                                                            className="hidden"
                                                            id="avatarInput"
                                                            disabled={uploadingAvatar}
                                                        />
                                                        <label
                                                            htmlFor="avatarInput"
                                                            className="block relative w-36 h-36 rounded-xl overflow-hidden border-2 border-white shadow-lg cursor-pointer"
                                                        >
                                                            {avatarPreview || profile.avatarUrl ? (
                                                                <img
                                                                    src={avatarPreview || `${APP_CONFIG.uploadsBaseUrl}${profile.avatarUrl}`}
                                                                    alt="Avatar"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center bg-brand-blue">
                                                                    <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                    </svg>
                                                                </div>
                                                            )}

                                                            {/* Overlay com ícone de editar no hover */}
                                                            {!uploadingAvatar && (
                                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <div className="text-center">
                                                                        <svg className="w-8 h-8 text-white mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                        </svg>
                                                                        <span className="text-xs text-white font-medium">Editar</span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Loading overlay */}
                                                            {uploadingAvatar && (
                                                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                                    <svg className="animate-spin h-8 w-8 text-white" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </label>

                                                        {/* Botão X para remover avatar (quando existir) */}
                                                        {profile.avatarUrl && !uploadingAvatar && (
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveAvatar}
                                                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all transform hover:scale-110 z-10"
                                                                title="Remover avatar"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Campos de Nome à direita */}
                                                    <div className="flex-1 space-y-4 w-full">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-0">
                                                                Primeiro Nome
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.firstName}
                                                                onChange={(e) => handleChange('firstName', e.target.value)}
                                                                maxLength={FIELD_LIMITS.firstName}
                                                                className="w-full px-4 py-2 rounded-xl transition-all shadow-sm bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                                                placeholder="João"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-0">
                                                                Último Nome
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.lastName}
                                                                onChange={(e) => handleChange('lastName', e.target.value)}
                                                                maxLength={FIELD_LIMITS.lastName}
                                                                className="w-full px-4 py-2 rounded-xl transition-all shadow-sm bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                                                placeholder="Silva"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Informações do Servidor */}
                                            <div className="bg-gradient-to-br from-brand-blue-light to-blue-50 rounded-2xl p-6 border border-blue-100">
                                                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                    <span className="text-xl">🎮</span>
                                                    Informações do Servidor
                                                </h4>

                                                {/* Alerta informativo quando não tem compra */}
                                                {!hasPurchase && (
                                                    <div className="mb-4 bg-white/60 backdrop-blur-sm border border-blue-200 rounded-xl p-3 shadow-sm">
                                                        <div className="flex gap-2">
                                                            <svg className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                            </svg>
                                                            <p className="text-xs text-gray-700 font-medium">
                                                                As informações do servidor ficam disponíveis após realizar sua primeira compra no site.
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Layout compacto com logo e campos ao lado */}
                                                <div className="flex flex-col md:flex-row gap-4 items-start">
                                                    {/* Logo do Servidor com hover para editar */}
                                                    <div className="relative group flex-shrink-0">
                                                        <input
                                                            type="file"
                                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                                            onChange={handleServerLogoChange}
                                                            className="hidden"
                                                            id="serverLogoInput"
                                                            disabled={uploadingServerLogo || !hasPurchase}
                                                        />
                                                        <label
                                                            htmlFor={hasPurchase ? "serverLogoInput" : undefined}
                                                            className={`block relative w-36 h-36 rounded-xl overflow-hidden border-2 border-white shadow-lg ${
                                                                !hasPurchase ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                                            }`}
                                                        >
                                                            {serverLogoPreview || profile.serverLogoUrl ? (
                                                                <img
                                                                    src={serverLogoPreview || `${APP_CONFIG.uploadsBaseUrl}${profile.serverLogoUrl}`}
                                                                    alt="Logo do Servidor"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center bg-brand-blue">
                                                                    <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                                    </svg>
                                                                </div>
                                                            )}

                                                            {/* Overlay com ícone de editar no hover */}
                                                            {hasPurchase && !uploadingServerLogo && (
                                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <div className="text-center">
                                                                        <svg className="w-8 h-8 text-white mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                        </svg>
                                                                        <span className="text-xs text-white font-medium">Editar</span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Loading overlay */}
                                                            {uploadingServerLogo && (
                                                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                                    <svg className="animate-spin h-8 w-8 text-white" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </label>

                                                        {/* Botão X para remover logo (quando existir) */}
                                                        {profile.serverLogoUrl && hasPurchase && !uploadingServerLogo && (
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveServerLogo}
                                                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all transform hover:scale-110 z-10"
                                                                title="Remover logo"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Campos do Servidor ao lado da logo */}
                                                    <div className="flex-1 space-y-4 w-full">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-0">
                                                                Nome do Servidor
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.serverName}
                                                                onChange={(e) => handleChange('serverName', e.target.value)}
                                                                disabled={!hasPurchase}
                                                                maxLength={FIELD_LIMITS.serverName}
                                                                className={`w-full px-4 py-2 rounded-xl transition-all shadow-sm ${
                                                                    hasPurchase 
                                                                        ? 'bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue' 
                                                                        : 'bg-gray-100/80 border-2 border-gray-200 text-gray-600 cursor-not-allowed'
                                                                }`}
                                                                placeholder={hasPurchase ? "Ex: MeuServidor MC" : "Bloqueado até primeira compra"}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-0">
                                                                Website do Servidor
                                                            </label>
                                                            <input
                                                                type="url"
                                                                value={profile.serverWebsite}
                                                                onChange={(e) => handleChange('serverWebsite', e.target.value)}
                                                                disabled={!hasPurchase}
                                                                maxLength={FIELD_LIMITS.serverWebsite}
                                                                className={`w-full px-4 py-2 rounded-xl transition-all shadow-sm ${
                                                                    hasPurchase 
                                                                        ? 'bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue' 
                                                                        : 'bg-gray-100/80 border-2 border-gray-200 text-gray-600 cursor-not-allowed'
                                                                }`}
                                                                placeholder={hasPurchase ? "https://meuservidor.com" : "Bloqueado até primeira compra"}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contato */}
                                            <div className="bg-gradient-to-br from-brand-blue-light to-blue-50 rounded-2xl p-6 border border-blue-100">
                                                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                    <span className="text-xl">📞</span>
                                                    Contato
                                                </h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Telefone
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            value={profile.phone}
                                                            onChange={(e) => handleChange('phone', e.target.value)}
                                                            maxLength={FIELD_LIMITS.phone}
                                                            className="w-full px-4 py-2 rounded-xl transition-all shadow-sm bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                                            placeholder="(11) 99999-9999"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Email Alternativo
                                                        </label>
                                                        <input
                                                            type="email"
                                                            value={profile.alternativeEmail}
                                                            onChange={(e) => handleChange('alternativeEmail', e.target.value)}
                                                            maxLength={FIELD_LIMITS.alternativeEmail}
                                                            className="w-full px-4 py-2 rounded-xl transition-all shadow-sm bg-white border-2 border-blue-200 text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                                            placeholder="contato@exemplo.com"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Conta Discord
                                                        </label>
                                                        {profile.discordUsername ? (
                                                            <div className="w-full px-4 py-2 rounded-xl bg-gray-100 border-2 border-gray-200 text-gray-700 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                                                                </svg>
                                                                {profile.discordUsername}
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-2">
                                                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                                                    <div className="flex items-start gap-2">
                                                                        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                                        </svg>
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-amber-800">Conta Discord não vinculada</p>
                                                                            <p className="text-xs text-amber-700 mt-1">
                                                                                Use o comando <code className="bg-amber-100 px-1 py-0.5 rounded text-amber-800 font-mono">/vincular</code> no Discord para vincular sua conta.
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="text"
                                                                        value={profile.linkingCode}
                                                                        readOnly
                                                                        className="flex-1 px-4 py-2 rounded-xl bg-gray-100 border-2 border-gray-200 text-gray-700 font-mono text-center"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => navigator.clipboard.writeText(profile.linkingCode)}
                                                                        className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors flex items-center gap-1"
                                                                        title="Copiar código"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Endereço */}
                                            <div className="bg-gradient-to-br from-brand-blue-light to-blue-50 rounded-2xl p-6 border border-blue-100">
                                                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                    <span className="text-xl">🏠</span>
                                                    Endereço
                                                </h4>
                                                <div className="space-y-4">
                                                    {/* Alerta informativo */}
                                                    <div className="bg-white/60 backdrop-blur-sm border border-blue-200 rounded-xl p-3 shadow-sm">
                                                        <div className="flex gap-2">
                                                            <svg className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <p className="text-xs text-gray-700 font-medium">
                                                                Digite o CEP para preencher automaticamente o endereço. Você precisará informar apenas o número e complemento.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* CEP em destaque */}
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-800 mb-2">
                                                            CEP *
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="text"
                                                                value={profile.addressZipCode}
                                                                onChange={(e) => handleCepChange(e.target.value)}
                                                                className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all shadow-sm"
                                                                placeholder="00000-000"
                                                                maxLength={9}
                                                            />
                                                            {loadingCep && (
                                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                                    <svg className="animate-spin h-5 w-5 text-brand-blue" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Campos preenchidos automaticamente */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Rua
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressStreet}
                                                                readOnly
                                                                disabled
                                                                className="w-full px-4 py-3 bg-gray-100/80 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                                                                placeholder="Preenchido pelo CEP"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Bairro
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressNeighborhood}
                                                                readOnly
                                                                disabled
                                                                className="w-full px-4 py-3 bg-gray-100/80 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                                                                placeholder="Preenchido pelo CEP"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Cidade
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressCity}
                                                                readOnly
                                                                disabled
                                                                className="w-full px-4 py-3 bg-gray-100/80 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                                                                placeholder="Preenchido pelo CEP"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Estado
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressState}
                                                                readOnly
                                                                disabled
                                                                className="w-full px-4 py-3 bg-gray-100/80 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed"
                                                                placeholder="UF"
                                                                maxLength={2}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Campos editáveis pelo usuário */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-800 mb-2">
                                                                Número *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressNumber}
                                                                onChange={(e) => handleChange('addressNumber', e.target.value)}
                                                                maxLength={FIELD_LIMITS.addressNumber}
                                                                className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all shadow-sm"
                                                                placeholder="123"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Complemento
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={profile.addressComplement}
                                                                onChange={(e) => handleChange('addressComplement', e.target.value)}
                                                                maxLength={FIELD_LIMITS.addressComplement}
                                                                className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all shadow-sm"
                                                                placeholder="Apto 101, Bloco A, etc."
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Mensagens de erro/sucesso */}
                                            {error && (
                                                <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
                                                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-sm font-medium text-red-800 flex-1">{error}</p>
                                                </div>
                                            )}
                                            {success && (
                                                <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
                                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-sm font-medium text-green-800 flex-1">{success}</p>
                                                </div>
                                            )}
                                        </form>
                                    )}
                                </div>

                                {/* Footer com botões */}
                                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                        disabled={loading}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        onClick={handleSubmit}
                                        className="px-6 py-2.5 text-sm font-bold text-white bg-brand-blue rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:bg-gray-400 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none flex items-center gap-2"
                                        disabled={loading || fetchingProfile}
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Salvando...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Salvar Alterações
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

